import React, { useState } from 'react'

import SearchIcon from '@src/components/icons/Search'
import { ConfigLoadingStatus } from '@src/enums'
import { useFetchAllTokens } from '@src/hooks/useFetchAllTokens'
import { DISCORD_LINK, TELEGRAM_LINK } from '@src/layouts/Footer'
import { useConfigStore } from '@src/zustand_stores'

interface ChainSelectorPaneProps {
    searchResultsChain: Record<string, any>
    availableChainsFiltered: Record<string, any>
    selectedChain: string | number
    temporaryChainSelection?: number
    activeChainSelection?: number
    onChainClicked: (chainId: number) => void
    tokenQuery: string
    disabledChains?: number[]
    isNetworkSelectorOpen: boolean
    setIsNetworkSelectorOpen: (isOpen: boolean) => void
    side: 'buy' | 'sell'
}

const ChainSelectorPane = ({
    searchResultsChain,
    availableChainsFiltered,
    selectedChain,
    temporaryChainSelection,
    activeChainSelection,
    onChainClicked,
    tokenQuery,
    disabledChains,
    isNetworkSelectorOpen,
    setIsNetworkSelectorOpen,
    side,
}: ChainSelectorPaneProps): JSX.Element => {
    const [networkFilterQuery, setNetworkFilterQuery] = useState('')

    const configLoading = useConfigStore(config => config.loading)
    const { isLoading: zustandTokensLoading } = useFetchAllTokens()

    const isConfigurationLoading = React.useMemo(
        () =>
            configLoading === ConfigLoadingStatus.loading_not_yet_started ||
            configLoading === ConfigLoadingStatus.loading ||
            zustandTokensLoading,
        [configLoading, zustandTokensLoading]
    )

    // Filter networks based on the search query
    const filteredNetworks = Object.entries(availableChainsFiltered).filter(([_, chain]) =>
        chain.name.toLowerCase().includes(networkFilterQuery.toLowerCase())
    )

    const handlePasteAddress = async () => {
        try {
            const text = await navigator.clipboard.readText()
            setNetworkFilterQuery(text)
        } catch (err) {
            console.error('Failed to read clipboard contents: ', err)
        }
    }

    // Check if we're doing an address search
    const isAddressSearch = tokenQuery && (tokenQuery.startsWith('0x') || tokenQuery.length >= 40)

    // Determine which chains to display
    const chainsToDisplay = tokenQuery ? searchResultsChain : availableChainsFiltered

    // Check if the current chain is valid for this search
    const currentChain = temporaryChainSelection ?? selectedChain
    const isCurrentChainInSearchResults = isAddressSearch
        ? Object.keys(searchResultsChain).includes(String(currentChain))
        : true

    // Network selection content
    const renderNetworkSelectorContent = (): JSX.Element => {
        // If configuration is loading, show placeholder content
        if (isConfigurationLoading) {
            return (
                <div className='flex h-full flex-col transition-transform duration-300 ease-in-out'>
                    {/* Search input placeholder */}
                    <div className='px-4 py-3'>
                        <div className='relative flex w-full items-center rounded-md bg-mach-darkBlue px-3 py-2.5'>
                            <SearchIcon className='mr-2 h-5 w-5 flex-shrink-0 text-token-modal-input-placeholder' />
                            <div className='h-5 flex-1 animate-pulse rounded bg-neutral-700'></div>
                            <div className='ml-2 h-8 w-16 animate-pulse rounded-md bg-neutral-700'></div>
                        </div>
                    </div>

                    {/* Networks list placeholders */}
                    <div className='flex-1 overflow-y-auto px-4 pb-6'>
                        <div className='space-y-3'>
                            {Array.from({ length: 6 }).map((_, index) => (
                                <div
                                    key={`network-placeholder-${index}`}
                                    className='flex animate-pulse items-center justify-between rounded-lg bg-transparent p-3'
                                >
                                    <div className='flex items-center gap-3'>
                                        <div className='h-8 w-8 rounded-full bg-neutral-700'></div>
                                        <div className='h-5 w-24 rounded bg-neutral-700'></div>
                                    </div>
                                    <div className='h-6 w-20 rounded bg-neutral-700'></div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )
        }
        return (
            <div className={`flex h-full flex-col transition-transform duration-300 ease-in-out`}>
                {/* Search input */}
                <div className='px-4 py-3'>
                    <div className='relative flex w-full items-center rounded-md bg-mach-darkBlue px-3 py-2.5'>
                        <SearchIcon className='mr-2 h-5 w-5 flex-shrink-0 text-token-modal-input-placeholder' />
                        <input
                            type='text'
                            placeholder='Type network'
                            value={networkFilterQuery}
                            onChange={e => setNetworkFilterQuery(e.target.value)}
                            className='w-full bg-transparent text-white placeholder-token-modal-input-placeholder outline-none'
                        />
                        {networkFilterQuery ? (
                            <button
                                className='ml-2 flex-shrink-0 rounded-md bg-mach-gray px-3 py-1 text-sm text-white transition-colors hover:brightness-110'
                                onClick={() => setNetworkFilterQuery('')}
                            >
                                Clear
                            </button>
                        ) : (
                            <button
                                className='ml-2 flex-shrink-0 rounded-md bg-mach-gray px-3 py-1 text-sm text-white transition-colors hover:brightness-110'
                                onClick={handlePasteAddress}
                            >
                                Paste
                            </button>
                        )}
                    </div>
                </div>

                {/* Networks list or no results */}
                <div className='flex-1 overflow-y-auto px-4 pb-6'>
                    {filteredNetworks.length > 0 ? (
                        <div className='space-y-3'>
                            {filteredNetworks.map(([networkId, chain]) => {
                                const isSelected = +networkId === (temporaryChainSelection ?? selectedChain)
                                const isDisabled = disabledChains?.includes(+networkId) && +networkId !== +selectedChain

                                return (
                                    <button
                                        key={`network-selector-${networkId}`}
                                        onClick={() => {
                                            onChainClicked(+networkId)
                                            setNetworkFilterQuery('') // Clear filter when selecting
                                            setIsNetworkSelectorOpen(false)
                                        }}
                                        disabled={isDisabled}
                                        className={`flex w-full items-center gap-2 rounded-lg p-3 transition-colors ${
                                            isSelected
                                                ? 'bg-token-modal-chain-hover text-white hover:bg-token-modal-chain-hover'
                                                : 'text-white hover:bg-token-modal-chain-hover'
                                        } ${isDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                                    >
                                        <img src={chain.icon} alt={chain.name} className='h-8 w-8 rounded-full' />
                                        <span className='flex text-base font-medium'>{chain.name}</span>
                                        {isSelected && <div className='ml-auto h-2 w-2 rounded-full bg-white'></div>}
                                    </button>
                                )
                            })}
                        </div>
                    ) : (
                        <div className='flex h-full flex-col items-center justify-center px-6 py-8 text-center'>
                            <h2 className='mb-4 text-4xl font-bold text-white'>No networks found</h2>
                            {side === 'sell' ? (
                                <>
                                    <p className='mb-8 max-w-md text-lg text-[#8B8B93]'>
                                        Seems like there is no such network available. Please check your spelling or try
                                        a different network name
                                    </p>
                                    <div className='flex gap-4'>
                                        <button
                                            className='flex items-center gap-2 rounded-lg rounded-md bg-mach-gray px-6 py-3 text-white transition-colors hover:bg-opacity-80'
                                            onClick={() => window.open(TELEGRAM_LINK, '_blank')}
                                        >
                                            Join Telegram
                                            <svg
                                                width='24'
                                                height='24'
                                                viewBox='0 0 24 24'
                                                fill='white'
                                                xmlns='http://www.w3.org/2000/svg'
                                            >
                                                <path d='M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM16.64 8.8C16.49 10.38 15.84 14.22 15.51 15.99C15.37 16.74 15.09 16.99 14.83 17.02C14.25 17.07 13.81 16.64 13.25 16.27C12.37 15.69 11.87 15.33 11.02 14.77C10.03 14.12 10.67 13.76 11.24 13.18C11.39 13.03 13.95 10.7 14 10.49C14.0069 10.4476 14.0022 10.4043 13.9865 10.3647C13.9708 10.325 13.9447 10.2903 13.91 10.264C13.83 10.214 13.72 10.235 13.63 10.252C13.51 10.274 12.06 11.25 9.27 13.18C8.86 13.45 8.49 13.58 8.17 13.57C7.81 13.56 7.12 13.36 6.62 13.19C6.01 12.98 5.53 12.87 5.57 12.53C5.59 12.35 5.83 12.17 6.29 11.98C9.3 10.64 11.3 9.76 12.3 9.33C15.18 7.93 15.85 7.69 16.29 7.69C16.38 7.69 16.59 7.71 16.73 7.83C16.84 7.92 16.86 8.05 16.87 8.14C16.87 8.21 16.89 8.46 16.64 8.8Z' />
                                            </svg>
                                        </button>
                                        <button
                                            className='flex items-center gap-2 rounded-lg rounded-md bg-mach-gray px-6 py-3 text-white transition-colors hover:bg-opacity-80'
                                            onClick={() => window.open(DISCORD_LINK, '_blank')}
                                        >
                                            Join Discord
                                            <svg
                                                width='24'
                                                height='24'
                                                viewBox='0 0 24 24'
                                                fill='white'
                                                xmlns='http://www.w3.org/2000/svg'
                                            >
                                                <path d='M19.73 4.87C18.33 4.22 16.85 3.76 15.3 3.5C15.13 3.8 14.94 4.21 14.81 4.53C13.17 4.29 11.55 4.29 9.95 4.53C9.82 4.21 9.62 3.8 9.45 3.5C7.9 3.76 6.41 4.23 5.01 4.88C2.44 8.76 1.75 12.53 2.1 16.24C3.88 17.54 5.6 18.35 7.29 18.89C7.71 18.3 8.08 17.67 8.39 17.01C7.73 16.78 7.11 16.48 6.52 16.12C6.65 16.02 6.77 15.92 6.89 15.81C10.65 17.57 14.75 17.57 18.47 15.81C18.59 15.92 18.71 16.02 18.84 16.12C18.25 16.48 17.62 16.78 16.97 17.01C17.28 17.67 17.65 18.3 18.07 18.89C19.76 18.35 21.48 17.54 23.26 16.24C23.67 11.89 22.57 8.15 19.73 4.87ZM8.75 13.95C7.68 13.95 6.8 12.94 6.8 11.71C6.8 10.48 7.66 9.47 8.75 9.47C9.84 9.47 10.72 10.48 10.7 11.71C10.7 12.94 9.84 13.95 8.75 13.95ZM15.61 13.95C14.54 13.95 13.66 12.94 13.66 11.71C13.66 10.48 14.52 9.47 15.61 9.47C16.7 9.47 17.58 10.48 17.56 11.71C17.56 12.94 16.7 13.95 15.61 13.95Z' />
                                            </svg>
                                        </button>
                                    </div>
                                </>
                            ) : (
                                <p className='mb-8 max-w-md text-lg text-[#8B8B93]'>
                                    Seems like there is no such network available. Please check your spelling or try a
                                    different network name
                                </p>
                            )}
                        </div>
                    )}
                </div>
            </div>
        )
    }

    return (
        <div className='relative flex h-full w-full flex-col'>
            {/* Only show the large networks selector UI or the compact view, not both */}
            {!isNetworkSelectorOpen && (
                <>
                    <h3 className='mb-3 text-base font-medium text-token-modal-label-text'>
                        {Object.keys(chainsToDisplay).length ? 'Networks' : 'No Networks Available'}
                    </h3>
                    <div className='chain-selector-scroll flex w-full items-center gap-4 overflow-x-auto pb-1'>
                        {isConfigurationLoading ? (
                            // Show loading placeholders when configuration is loading
                            <>
                                {/* Selected network button placeholder */}
                                <div className='flex min-w-fit animate-pulse items-center gap-2 rounded-[10px] bg-neutral-700 px-3 py-2'>
                                    <div className='flex items-center p-0.5'>
                                        <div className='h-5 w-5 rounded-full bg-neutral-600'></div>
                                    </div>
                                    <div className='h-5 w-20 rounded bg-neutral-600'></div>
                                </div>

                                {/* Small chain icons placeholders */}
                                <div className='flex items-center gap-0.5'>
                                    {Array.from({ length: 3 }).map((_, index) => (
                                        <div
                                            key={`chain-icon-placeholder-${index}`}
                                            className='flex h-9 w-9 flex-shrink-0 animate-pulse items-center justify-center rounded-full bg-transparent p-2'
                                        >
                                            <div className='h-5 w-5 rounded-full bg-neutral-700'></div>
                                        </div>
                                    ))}
                                </div>

                                {/* +X more button placeholder */}
                                <div className='ml-auto flex min-w-fit flex-shrink-0 animate-pulse items-center gap-1 rounded-lg bg-neutral-700 px-3 py-2'>
                                    <div className='h-4 w-12 rounded bg-neutral-600'></div>
                                </div>
                            </>
                        ) : tokenQuery ? (
                            <div className='flex items-center gap-2'>
                                {Object.entries(chainsToDisplay).map(([networkId, chain]) => (
                                    <button
                                        key={`mobile-chain-icon-${networkId}`}
                                        className={`flex flex-shrink-0 cursor-pointer items-center justify-center rounded-full p-2 transition-colors hover:brightness-110 ${
                                            +networkId === (temporaryChainSelection ?? selectedChain)
                                                ? 'bg-token-modal-chain-hover'
                                                : ''
                                        }`}
                                        onClick={() => onChainClicked(+networkId)}
                                        disabled={disabledChains?.includes(+networkId) && +networkId !== +selectedChain}
                                    >
                                        <img src={chain.icon} alt={chain.name} className='h-5 w-5 rounded-full' />
                                    </button>
                                ))}
                            </div>
                        ) : (
                            <>
                                {/* Selected network button with white background - Only show if not in search results */}
                                {Object.keys(chainsToDisplay).length > 0 &&
                                    isCurrentChainInSearchResults &&
                                    availableChainsFiltered[currentChain] && (
                                        <button
                                            className='flex min-w-fit cursor-pointer items-center gap-2 rounded-[10px] bg-white px-3 py-2 transition-colors hover:brightness-110'
                                            onClick={() => onChainClicked(+currentChain)}
                                        >
                                            <div className='flex items-center p-0.5'>
                                                <img
                                                    src={availableChainsFiltered[currentChain]?.icon}
                                                    alt={availableChainsFiltered[currentChain]?.name}
                                                    className='h-5 w-5 rounded-full'
                                                />
                                            </div>
                                            <span className='hidden whitespace-nowrap text-base font-medium text-black min-[420px]:inline'>
                                                {availableChainsFiltered[currentChain]?.name}
                                            </span>
                                        </button>
                                    )}

                                {/* Container for small chain icons - Only show chains that aren't the current one */}
                                <div className='flex items-center gap-0.5'>
                                    {/* Other networks - Show chains from search results that aren't the current chain */}
                                    {Object.entries(chainsToDisplay)
                                        .filter(
                                            ([networkId]) =>
                                                +networkId !== currentChain || !isCurrentChainInSearchResults
                                        )
                                        .slice(0, 4)
                                        .map(([networkId, chain]) => (
                                            <button
                                                key={`chain-icon-${networkId}`}
                                                className={`flex h-9 w-9 flex-shrink-0 cursor-pointer items-center justify-center rounded-full p-2 transition-colors hover:brightness-110 ${
                                                    +networkId === (temporaryChainSelection ?? selectedChain)
                                                        ? 'bg-token-modal-chain-hover'
                                                        : ''
                                                }`}
                                                onClick={() => onChainClicked(+networkId)}
                                                disabled={
                                                    disabledChains?.includes(+networkId) &&
                                                    +networkId !== +selectedChain
                                                }
                                            >
                                                <img
                                                    src={chain.icon}
                                                    alt={chain.name}
                                                    className='h-5 w-5 rounded-full'
                                                />
                                            </button>
                                        ))}
                                </div>

                                {/* "+X more" button if we have more networks to show */}
                                {Object.keys(chainsToDisplay).length > 4 && (
                                    <button
                                        className='ml-auto flex min-w-fit flex-shrink-0 items-center gap-1 rounded-lg bg-mach-gray px-3 py-2 text-sm font-medium text-white transition-colors hover:brightness-110'
                                        onClick={() => setIsNetworkSelectorOpen(true)}
                                    >
                                        <span className='whitespace-nowrap'>
                                            +{Object.keys(chainsToDisplay).length - 4} more
                                        </span>
                                    </button>
                                )}
                            </>
                        )}
                    </div>
                </>
            )}

            {/* Render the network selector content when open */}
            {isNetworkSelectorOpen && renderNetworkSelectorContent()}
        </div>
    )
}

export default ChainSelectorPane
